import { geometrySVC } from "geometry/service"
import { normalizeSync } from "normalize-diacritics"
import { IGeoShapeCity, IGeoShapeMetropolitan, SHAPE_TYPES } from "shape/model"
import { shapeRPO } from "shape/repository"

/**
 * The METROPOLITAN functionality is a hack.
 *
 * It will be refactored one day in order to be implemented server side.
 * From now it's just a frontend hack.
 *
 * For this reason everything related to metropolitans will stay in this file
 * (except the code in the components)
 *
 * So the const/interfaces used only for metropolitan will stay here and not
 * in the model.ts as usually.
 */

const MONTREAL_METROPOLITAN_KEY = "campaign.geo-targets.shape.metropolitan.montreal.name"
const TORONTO_METROPOLITAN_KEY = "campaign.geo-targets.shape.metropolitan.toronto.name"
const VANCOUVER_METROPOLITAN_KEY = "campaign.geo-targets.shape.metropolitan.vancouver.name"

const MONTREAL_METROPOLITAN_CITY_SHAPE_IDS = [
  33192, 33193, 32725, 32696, 32726, 32745, 32727, 32728, 32716, 32717, 33227, 33217, 32603, 33306, 32641, 32646, 33195,
  32735, 32642, 32640, 32639, 32643, 32531, 32532, 32645, 32644, 32652, 32651, 32638, 32621, 32566, 32635, 32634, 32622,
  32565, 33251, 33211, 33194, 32655, 32637, 32659, 32636, 32657, 32653, 33250, 32706, 33248, 33249, 32633, 32654, 32841,
  32708, 32707, 32699, 32656, 32658, 32723, 32701, 32709, 32842, 32724, 32702, 32710, 32703, 32681, 32697, 32682, 32712,
  32680, 32720, 33253, 33330, 32711, 33252, 32718, 32685, 33238, 32713, 32705, 32719, 32744, 33331, 33532, 32698, 32715,
  32683, 32714, 33254, 33533, 33271, 33500, 32741, 32742, 32700, 33272, 33332, 33219, 32684, 33218, 33273, 32743, 33234,
  33220, 33270, 33292, 33262, 33186, 33274, 33294, 33228, 33293, 33291, 33391, 33245, 33275, 33173, 33256, 33263, 33235,
  33264, 33255, 33276, 33367, 32768, 32767, 32769, 32766, 33366, 33246, 33296, 33265, 33295, 32765, 33268, 33368
]

const TORONTO_METROPOLITAN_CITY_SHAPE_IDS = [
  33692, 33691, 33697, 33677, 33686, 33685, 33852, 33687, 33851, 33881, 33876, 33877, 33880, 33879, 33882, 33850, 34088,
  34145, 33878, 34091, 33883, 33716, 33884, 33717, 33772, 33719, 33885, 33700, 33718, 33890, 33728, 33668, 34036, 33888,
  33886, 33773, 34086, 34096, 34033
]

const VANCOUVER_METROPOLITAN_CITY_SHAPE_IDS = [
  35854, 35457, 35454, 35456, 35455, 35450, 35448, 35447, 35467, 30039, 35458, 35452, 35846, 35849, 35551, 35549, 35548,
  35468, 35532, 35550, 35830, 35829, 35465, 35888, 35533, 35536, 35537, 35832, 35831, 35538, 35539, 35884, 35466, 35534,
  35540, 35698, 35535, 35887, 35885, 35881, 35886
]

export function searchMetropolitans(search: string): IGeoShapeMetropolitan[] {
  if (!search) {
    return []
  }
  const normalizedSearch = normalizeSync(search).toLowerCase()
  const result: IGeoShapeMetropolitan[] = []
  const metropolitans = getMetropolitansWithoutShape()
  for (const metropolitan of metropolitans) {
    if (metropolitan.normalizedName.includes(normalizedSearch)) {
      result.push(metropolitan)
    }
  }
  return result
}

export function getMetropolitansWithoutShape(): IGeoShapeMetropolitan[] {
  return [
    {
      id: 0,
      shape_id: "montreal",
      labelKey: MONTREAL_METROPOLITAN_KEY,
      normalizedName: "greater montreal area gma region metropolitaine de montreal rgm",
      shape: undefined as any,
      type: SHAPE_TYPES.METROPOLITAN,
      cityIds: MONTREAL_METROPOLITAN_CITY_SHAPE_IDS.slice(),
      cities: [],
      country_id: "CA",
      state_id: "QC"
    },
    {
      id: 1,
      shape_id: "toronto",
      labelKey: TORONTO_METROPOLITAN_KEY,
      normalizedName: "greater toronto area gta region du grand toronto rgt",
      shape: undefined as any,
      type: SHAPE_TYPES.METROPOLITAN,
      cityIds: TORONTO_METROPOLITAN_CITY_SHAPE_IDS.slice(),
      cities: [],
      country_id: "CA",
      state_id: "ON"
    },
    {
      id: 2,
      shape_id: "vancouver",
      labelKey: VANCOUVER_METROPOLITAN_KEY,
      normalizedName: "greater vancouver area gva grand vancouver metro vancouver",
      shape: undefined as any,
      type: SHAPE_TYPES.METROPOLITAN,
      cityIds: VANCOUVER_METROPOLITAN_CITY_SHAPE_IDS.slice(),
      cities: [],
      country_id: "CA",
      state_id: "BC"
    }
  ]
}

export const getCityMetropolitan = (cityId: number): string => {
  if (MONTREAL_METROPOLITAN_CITY_SHAPE_IDS.contains(cityId)) {
    return MONTREAL_METROPOLITAN_KEY
  }
  if (TORONTO_METROPOLITAN_CITY_SHAPE_IDS.contains(cityId)) {
    return TORONTO_METROPOLITAN_KEY
  }
  if (VANCOUVER_METROPOLITAN_CITY_SHAPE_IDS.contains(cityId)) {
    return VANCOUVER_METROPOLITAN_KEY
  }
  return ""
}

export function getMetropolitanWithShape(metropolitan: IGeoShapeMetropolitan): Promise<IGeoShapeMetropolitan> {
  return new Promise<IGeoShapeMetropolitan>((resolve, reject) => {
    const toProcessCitiesCount = metropolitan.cityIds.length
    let processedCities = 0
    const cities: IGeoShapeCity[] = []
    const onFinish = (hasError?: boolean) => {
      if (hasError) {
        return reject(`Unable to get all cities of metropolitran "${metropolitan.labelKey}", unexpected error`)
      }
      if (++processedCities === toProcessCitiesCount) {
        metropolitan.cities = cities
        metropolitan.shape = geometrySVC.getMultiPolygonFeature(
          cities.map(c => c.shape),
          {}
        ).geometry as any
        resolve(metropolitan)
      }
    }
    for (const cityId of metropolitan.cityIds) {
      shapeRPO
        .getCityShapeData(cityId)
        .then(cityShapeData => {
          cities.push({
            ...cityShapeData,
            id: cityId,
            type: SHAPE_TYPES.CITY
          })
          onFinish()
        })
        .catch(error => {
          console.error(error)
          onFinish(true)
        })
    }
  })
}

export function getCitiesWithoutMetropolitanCities(
  cities: IGeoShapeCity[],
  metropolitans: IGeoShapeMetropolitan[]
): IGeoShapeCity[] {
  const allMetropolitanCityIds = metropolitans.map(m => m.cityIds).flat()
  return cities.filter(c => !allMetropolitanCityIds.includes(c.city_shape_id))
}

export function getMetropolitansFromCities(cities: IGeoShapeCity[]): IGeoShapeMetropolitan[] {
  if (cities.length === 0) {
    return []
  }
  const cityIds = cities.map(c => c.city_shape_id)
  const cityById: { [cityId: number]: IGeoShapeCity } = {}
  for (const city of cities) {
    cityById[city.city_shape_id] = city
  }
  const metropolitans = getMetropolitansWithoutShape()
  const result: IGeoShapeMetropolitan[] = []
  for (const metropolitan of metropolitans) {
    if (metropolitan.cityIds.every(id => cityIds.includes(id))) {
      metropolitan.cities = metropolitan.cityIds.map(id => cityById[id])
      metropolitan.shape = geometrySVC.getMultiPolygonFeature(
        metropolitan.cities.map(c => c.shape),
        {}
      ).geometry as any
      result.push(metropolitan)
    }
  }
  return result
}
